import * as React from 'react';
import './CreateEditFolderDialog.scss';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Dialog, DialogUtils } from '@shared/ui-react/Dialog';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { Notify } from '@shared/ui-native/Notify';
import { Events } from '@shared/Events';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { FormErrors, FormGroup } from '@shared/ui-react/UtilComponents';
import { cs } from '@shared/utils/utils-classname';
import { Button } from '@shared/ui-react/Button';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Ajax } from '@shared/Ajax';
import { FoldersService } from '@shared/services/FoldersService';
import { PresentableLabel } from '@shared/files/ui/PresentableLabel';

export const CreateEditFolderDialog = {
    create: (initialParentFolderId: number, treeData: ITreeData, onSuccess: (folder: Partial<IFolder>, parentId: number) => void) => {
        treeData = JSON.parse(JSON.stringify(treeData));
        ReactUtils.safeRender(
            <Dialog onClose={DialogUtils.close} title={__('js_files.create_folder')}>
                <CreateFolderDialogComponent initialParentFolderId={initialParentFolderId} onSuccess={onSuccess} treeData={treeData} />
            </Dialog>,
            document.getElementById('react-portal-root')
        );
    },
    edit: (folder: any) => {
        ReactUtils.safeRender(
            <Dialog onClose={DialogUtils.close} title={__('general.edit')}>
                <CreateFolderDialogComponent editFolder={folder} />
            </Dialog>,
            document.getElementById('react-portal-root')
        );
    },
    info: (folder: any) => {
        ReactUtils.safeRender(
            <Dialog onClose={DialogUtils.close} title={__('general.show_details')}>
                <CreateFolderDialogComponent editFolder={folder} readOnly={true} />
            </Dialog>,
            document.getElementById('react-portal-root')
        );
    },
};

@observer
class CreateFolderDialogComponent extends React.Component<{
    initialParentFolderId?: number;
    onSuccess?: (folder: Partial<IFolder>, parentId: number) => void;
    editFolder?: any;
    treeData?: any;
    readOnly?: boolean;
}> {
    @observable parentFolderId = null;
    @observable name = '';
    @observable description = '';
    @observable isLoading = false;
    @observable presentable = true;
    @observable alignmentId = null;
    @observable limitLower = null;
    @observable limitUpper = null;
    @observable alignments = [];
    @observable weight = null;
    initialPresentable = null;

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        if (this.props.editFolder) {
            this.isLoading = true;
            FoldersService.getDetails(this.props.editFolder.id).done(({ folder }) => {
                this.name = folder.name;
                this.description = folder.description;
                this.alignmentId = folder.alignment;
                this.presentable = folder.presentable;
                this.initialPresentable = folder.presentable;
                this.limitLower = folder.toleranceLowerLimit;
                this.limitUpper = folder.toleranceUpperLimit;
                this.weight = folder.weight || 1;

                Ajax({
                    url: 'ajax_project_alignments.json',
                })
                    .done(({ alignments }) => {
                        this.alignments = alignments;
                    })
                    .always(() => (this.isLoading = false));
            });
        } else {
            this.parentFolderId = this.props.initialParentFolderId || kuura.getRootFolderId();
        }
    }

    render() {
        const isEdit = this.props.editFolder?.id;
        const readOnly = this.props.readOnly;

        const EditForm = (
            <>
                <div className='form-group'>
                    <label>
                        <PresentableLabel />
                    </label>
                    <InputSwitch checked={this.presentable} disabled={readOnly} onChange={e => (this.presentable = e.value)} />
                </div>
                <div className='form-group'>
                    <label>{kuura.getMessage('general.alignment')}</label>
                    <select value={this.alignmentId} onChange={e => (this.alignmentId = e.currentTarget.value)} disabled={readOnly}>
                        <option value='-1'>---</option>
                        {(this.alignments || []).map(a => (
                            <option value={a.id}>{a.name}</option>
                        ))}
                    </select>
                </div>
                <h6>{__('js_models.folder_tolerance')}</h6>
                <FormGroup title={kuura.getMessage('js_models.folder_tolerance_upper_limit')} row>
                    <div className='flex-row ai-center'>
                        <InputNumber
                            value={this.limitUpper}
                            readOnly={readOnly}
                            onValueChange={e => (this.limitUpper = e.value)}
                            useGrouping={false}
                            step={0.01}
                            maxFractionDigits={5}
                            style={{ width: 120 }}
                        />
                        <span>&nbsp;m</span>
                    </div>
                </FormGroup>
                <FormGroup title={kuura.getMessage('js_models.folder_tolerance_lower_limit')} row>
                    <div className='flex-row ai-center'>
                        <InputNumber
                            value={this.limitLower}
                            readOnly={readOnly}
                            onValueChange={e => (this.limitLower = e.value)}
                            useGrouping={false}
                            step={0.01}
                            maxFractionDigits={5}
                            style={{ width: 120 }}
                        />
                        <span>&nbsp;m</span>
                    </div>
                </FormGroup>
                <div className='form-group'>
                    <label>{kuura.getMessage('js_models.folder_weight')}</label>
                    <InputNumber
                        value={this.weight}
                        onValueChange={e => (this.weight = e.value)}
                        useGrouping={false}
                        readOnly={readOnly}
                        min={0}
                        max={100}
                        step={0.1}
                        style={{ width: 120 }}
                    />
                </div>
            </>
        );

        return (
            <div className={cs('padded', 'flex-column', 'create-folder__wrapper', { 'overlay-loading': this.isLoading })}>
                <div className='flex-row of-h'>
                    <div className='create-folder-dialog__icon'>
                        <Icon isEdit={isEdit} />
                    </div>
                    <div className='create-folder-dialog__form flex-column' style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                        <div className='form-group'>
                            <label>{kuura.getMessage('general.name')}</label>
                            <input type='text' value={this.name} onChange={e => (this.name = e.currentTarget.value)} readOnly={readOnly} autoFocus />
                            <FormErrors error={this.name.trim().length > 250 ? { type: 'maxLength', maxLength: 250 } : null} />
                        </div>

                        <div className='form-group'>
                            <label>{kuura.getMessage('general.description')}</label>
                            <textarea value={this.description} onChange={e => (this.description = e.currentTarget.value)} readOnly={readOnly} />
                        </div>

                        {!isEdit && (
                            <FormGroup title={kuura.getMessage('js_files.create_folder.parent_folder')}>
                                <FiletreeSelectReact
                                    selectable='folder'
                                    includeRoot={true}
                                    fetchAccess={true}
                                    treeData={this.props.treeData}
                                    selectedId={this.parentFolderId}
                                    onChange={id => (this.parentFolderId = id)}
                                />
                            </FormGroup>
                        )}

                        {isEdit ? EditForm : null}
                    </div>
                </div>

                <footer>
                    <hr />
                    <Button className='ik-button-secondary--subtle' onClick={DialogUtils.close}>
                        {__('general.close')}
                    </Button>
                    {!readOnly && (
                        <Button className='ik-button-primary right' loading={this.isLoading} disabled={!this.isValid || this.isLoading} onClick={this.createOrUpdate}>
                            {__(isEdit ? 'general.save' : 'general.create')}
                        </Button>
                    )}
                </footer>
            </div>
        );
    }

    createOrUpdate = () => {
        const isEdit = this.props.editFolder?.id;
        if (isEdit && this.initialPresentable !== this.presentable) {
            if (!confirm(__('js_files.presentable_change_confirm'))) {
                return;
            }
        }

        this.isLoading = true;

        let promise: JQueryPromise<any>;
        if (isEdit) {
            promise = FoldersService.edit({
                folderId: this.props.editFolder.id,
                folderName: this.name,
                description: this.description,
                presentable: this.presentable,
                alignmentId: this.alignmentId,
                toleranceUpperLimit: this.limitUpper,
                toleranceLowerLimit: this.limitLower,
                weight: this.weight,
            });
        } else {
            promise = FoldersService.create(this.parentFolderId, this.name, this.description);
        }

        promise
            .done(resp => {
                Notify.success();
                Events.trigger('tree-fetch');

                if (typeof this.props.onSuccess === 'function') {
                    this.props.onSuccess(resp?.folder, this.parentFolderId);
                }
                DialogUtils.close();
            })
            .fail(resp => {
                Notify.error(resp.errorMessages ? resp.errorMessages[0] : null);
            })
            .always(() => {
                this.isLoading = false;
            });
    };

    get isValid() {
        const trimName = this.name.trim();
        return trimName.length > 0 && trimName.length <= 250;
    }
}

const Icon = props => (
    <svg xmlns='http://www.w3.org/2000/svg' width={293.4} height={291} viewBox='0 0 1000 1000' xmlSpace='preserve'>
        <circle
            style={{
                fill: '#e7ecec',
            }}
            cx={146.8}
            cy={145.8}
            r={139.3}
        />
        <path
            style={{
                fillRule: 'evenodd',
                clipRule: 'evenodd',
                fill: '#117b9d',
            }}
            d='M234.6 107.5V218c0 6.6-5.4 12-12 12H50.9c-6.6 0-12-5.4-12-12V86.8c0-6.6 5.4-12 12-12h83.3c6.6 0 12 5.4 12 12v8.7h76.3c6.7 0 12.1 5.4 12.1 12z'
        />
        <path
            style={{
                fillRule: 'evenodd',
                clipRule: 'evenodd',
                fill: '#3a9fc0',
            }}
            d='M82 119.6h182.5c3.1-.2 5.7 2.2 5.8 5.3 0 .9-.1 1.8-.5 2.6l-30.2 94.7c-1.5 4.5-5.6 7.6-10.3 7.9H46.8c-3.1.2-5.7-2.2-5.8-5.3 0-.9.1-1.8.5-2.6l30.2-94.7c1.5-4.5 5.6-7.6 10.3-7.9z'
        />
        {!props.isEdit && (
            <path
                style={{
                    fillRule: 'evenodd',
                    clipRule: 'evenodd',
                    fill: '#73A812',
                    stroke: '#fff',
                    strokeWidth: 4,
                    strokeLinejoin: 'round',
                }}
                d='M267.7 202.6v2.3c0 6.6-5.4 12-12 12h-24.8V239c0 6.6-5.4 12-12 12h-2.4c-6.6 0-12-5.4-12-12v-22.2h-22.1c-6.6 0-12-5.4-12-12v-2.3c0-6.6 5.4-12 12-12h22.1v-24.8c0-6.6 5.4-12 12-12h2.4c6.6 0 12 5.4 12 12v24.8h24.8c6.6.1 12 5.4 12 12.1z'
            />
        )}
    </svg>
);
